import {useState, Fragment, useCallback} from "react";
import {PendingWrapper, useDebounceCallback, useHookClass} from "@dvrt/graphql-widgets";
import {fieldContentGenerators} from "@/components/call-list/CallListPage";
import {DataSource} from "./dataSource";
import Loading from "@/components/breadcrumbs/Loading";
import {Box, Stack, TextField, Button} from "@mui/material";
import {RecordList} from "@/components/RecordList";
import {defineMessage, FormattedMessage, useIntl} from "react-intl";
import {prepare_call_records_for_excel} from "./tools";
import {saveAs} from "file-saver";
import {useGroupNameLazyQuery} from "@/components/pickup-group/queries.generated";
import {useStructureConfig} from "@/context/StructureConfigProvider";

const inputStyle = {
    width: "-webkit-fill-available",
};

type Call = DataSource["queueCallRecords"][number];

const callToExcelRecord = (call: Call) => ({
    callTime: call.callTime,
    onBehalfOf: call.onBehalfOf,
    duration: call.duration,
    remoteNumber: call.remoteNumber,
    recording:
        call.recordingId && call.recordingStore && call.connected
            ? {
                  id: call.recordingId,
                  store: call.recordingStore,
              }
            : undefined,
});

type Props = {
    groupId: number;
};

export default function QueueCallListPage(props: Props) {
    const intl = useIntl();
    const dataSource = useHookClass(DataSource, props.groupId, false);
    const structureConfig = useStructureConfig();

    const [groupNameQuery] = useGroupNameLazyQuery({variables: {id: props.groupId}, fetchPolicy: "no-cache"});
    const [filterValue, setFilterValue] = useState<string>("");
    const [downloading, setDownloading] = useState<boolean>(false);

    const fetchData = useCallback(() => {
        dataSource.fetch(filterValue);
    }, [dataSource, filterValue]);
    useDebounceCallback(fetchData, 400);

    const downloadCallRecords = async () => {
        setDownloading(true);
        const {data: groupNameData} = await groupNameQuery();
        const groupName =
            groupNameData.groups?.items[0]?.name ||
            intl.formatMessage({id: "unknown", defaultMessage: "Unknown"}).toLowerCase();
        const [blob, filename] = await prepare_call_records_for_excel(
            dataSource.queueCallRecords.map(callToExcelRecord),
            groupName,
            structureConfig.chattoUrl,
            intl,
        );
        saveAs(blob, filename);
        setDownloading(false);
    };

    if (!dataSource.isInitialized) return <Loading />;

    return (
        <Fragment>
            <Stack alignItems="baseline" direction="row" spacing={{sm: 2, md: 8, lg: 24}}>
                <Box display="flex" flexGrow={1}>
                    <TextField
                        variant="standard"
                        style={inputStyle}
                        placeholder={intl.formatMessage({
                            id: "call_list.filterPlaceholder",
                            defaultMessage: "Local or remote number",
                        })}
                        onChange={(ev) => setFilterValue(ev.target.value)}
                    />
                </Box>
                <PendingWrapper pending={downloading}>
                    <Button onClick={downloadCallRecords} sx={{display: "flex"}}>
                        <FormattedMessage id="download" defaultMessage="Download" />
                    </Button>
                </PendingWrapper>
            </Stack>
            <RecordList<Call>
                records={dataSource.queueCallRecords}
                loading={false}
                style={{marginTop: 10}}
                initialOrder={["~callTime"]}
                fields={[
                    {
                        name: "onBehalfOf",
                        label: defineMessage({id: "call_list.caller", defaultMessage: "Caller"}),
                    },
                    {name: "remoteNumber", label: defineMessage({id: "call_list.agent", defaultMessage: "Agent"})},
                    {name: "callTime", label: defineMessage({id: "call_list.callTime", defaultMessage: "Time"})},
                    {name: "duration", label: defineMessage({id: "call_list.duration", defaultMessage: "Duration"})},
                    {
                        name: "recordingId",
                        label: defineMessage({id: "call_list.recording", defaultMessage: "Recording"}),
                    },
                ]}
                generators={fieldContentGenerators as Record<keyof Call, (call: Call) => any>}
            />
        </Fragment>
    );
}
